import { AnyAction } from 'redux';
import {
  changeSearchDialogState,
  NotificationModel,
  SearchDialogContainerStateChangeAction,
} from '@ps20-frontend/gui-components';
import {
  App,
  Immobilie,
  MultiangebotAdressenValidationResult,
  SearchlistItem,
  StandaloneProdukt,
  UploadedFile,
  VnWechselVertraegeAendern,
  Vorvertrag,
  UnderwriterMeasure,
  MeasureType,
  FileDownloadError,
} from '@ps20-frontend/frontend-models';
import { UwContextKey } from '@ps20-frontend/uw';
import { Injectable } from '@angular/core';
import {
  OperatingStandaloneVertragsdaten,
  OperatingStandardVertragsdaten,
  StandaloneVertragsuebersicht,
  StandardVertragsuebersicht,
} from '@ps20/bff-models';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  ModalType,
} from '@ps20-frontend/shop-integration-redux';
import { AngebotsdatumResetLogEntry } from '@ps20-frontend/migrations';
import { WarenkorbAngebot } from './reducers/produkte.reducer';
import { StandaloneProduktEinschliessen } from './model/standaloneProduktEinschliessen';
import { StandaloneDialogModel } from './model/standaloneDialog';
import { VertragItem } from './reducers/weitereVertraege.reducer';
import {
  UwEntryInitializePayload,
  UwEntryVertragZeichnenChangedPayload,
  UwEntryFilesUpdatedPayload,
  UwEntryTextsChangedPayload,
  UwDecisionSubmittedItem,
  UwDecisionMassnahmeAddedPayload,
  UwDecisionMassnahmeRemovedPayload,
  UwDecisionMassnahmeUpdatedPayload,
  UwDecisionDokumentationChangedPayload,
  UwDecisionUnderwriterInfoChangedPayload,
  UwDecisionUnderwriterInfoBatchChangedPayload,
  UnderwriterIdentityField,
  ProductUwSnapshot,
} from './model/uwDecisionPayloads';
import {
  ElementarfragebogenFilesDeletedPayload,
  DownloadElementarfragebogenPdfPayload,
} from './model/elementarfragebogenPayloads';

export interface DashboardAction<T> extends AnyAction {
  payload?: T;
}

@Injectable()
export class DashboardActions {
  static readonly HANDLE_HINWEISE_VORVERTRAEGE = 'HANDLE_HINWEISE_VORVERTRAEGE';
  static readonly ADD_NOTIFICATION = 'ADD_NOTIFICATION';
  static readonly DELETE_ANGEBOT_REQUESTED = 'DELETE_ANGEBOT_REQUESTED';
  static readonly TGV_PLACEHOLDER_ADDED = 'TGV_PLACEHOLDER_ADDED';
  static readonly TGV_PLACEHOLDER_REMOVED = 'TGV_PLACEHOLDER_REMOVED';
  static readonly TGV_GROUP_LAUFZEIT_REQUESTED = 'TGV_GROUP_LAUFZEIT_REQUESTED';
  static readonly DELETE_NOTIFICATION = 'DELETE_NOTIFICATION';
  static readonly IMMO_MODAL_OPEN = 'IMMO_MODAL_OPEN';
  static readonly IMMO_MODAL_CLOSE = 'IMMO_MODAL_CLOSE';
  static readonly IMMO_AUSGEWAEHLT = 'IMMO_AUSGEWAEHLT';
  static readonly NEUE_IMMO_AUSGEWAEHLT = 'NEUE_IMMO_AUSGEWAEHLT';
  static readonly STANDALONE_PRODUKT_MODAL_OPEN = 'STANDALONE_PRODUKT_MODAL_OPEN';
  static readonly UNF_PERS_MODAL_OPEN = 'UNF_PERS_MODAL_OPEN';
  static readonly UNF_PERS_MODAL_CLOSE = 'UNF_PERS_MODAL_CLOSE';
  static readonly UNF_PERS_AUSGEWAEHLT = 'UNF_PERS_AUSGEWAEHLT';
  static readonly VERMITTLERWECHSEL_MODAL_OPEN = 'VERMITTLERWECHSEL_MODAL_OPEN';
  static readonly VERMITTLERWECHSEL_MODAL_CLOSE = 'VERMITTLERWECHSEL_MODAL_CLOSE';
  static readonly STANDALONE_PRODUKT_MODAL_CLOSE = 'STANDALONE_PRODUKT_MODAL_CLOSE';
  static readonly STANDALONE_EINSCHLIESSEN_MODAL_OPEN = 'STANDALONE_EINSCHLIESSEN_MODAL_OPEN';
  static readonly STANDALONE_EINSCHLIESSEN_MODAL_CLOSE = 'STANDALONE_EINSCHLIESSEN_MODAL_CLOSE';
  static readonly RS_ZUSAMMENFUEHREN_MODAL_OPEN = 'RS_ZUSAMMENFUEHREN_MODAL_OPEN';
  static readonly RS_ZUSAMMENFUEHREN_MODAL_CLOSE = 'RS_ZUSAMMENFUEHREN_MODAL_CLOSE';
  static readonly EHV_MODAL_OPEN = 'EHV_MODAL_OPEN';
  static readonly EHV_MODAL_CLOSE = 'EHV_MODAL_CLOSE';
  static readonly SHOW_SOFORTSCHUTZ_HINWEISTEXT = 'SHOW_SOFORTSCHUTZ_HINWEISTEXT';
  static readonly KRAFT_RABATT_HINWEIS_CLOSED = 'KRAFT_RABATT_HINWEIS_CLOSED';
  static readonly UNFALL_SELECTED_CHANGED = 'UNFALL_SELECTED_CHANGED';
  static readonly NEW_IMMOBILIEN_RECEIVED = 'NEW_IMMOBILIEN_RECEIVED';
  static readonly STANDALONE_PRODUKT_EINSCHLIESSEN = 'STANDALONE_PRODUKT_EINSCHLIESSEN';
  static readonly REQUEST_DASHBOARD_MODAL = 'REQUEST_DASHBOARD_MODAL';
  static readonly REQUEST_FORWARD_TO_UNDERWRITER = 'REQUEST_FORWARD_TO_UNDERWRITER';
  static readonly FORWARD_TO_UNDERWRITER_STARTED = 'FORWARD_TO_UNDERWRITER_STARTED';
  static readonly FORWARD_TO_UNDERWRITER_FINISHED = 'FORWARD_TO_UNDERWRITER_FINISHED';
  static readonly REQUEST_FORWARD_TO_AGENT = 'REQUEST_FORWARD_TO_AGENT';
  static readonly FORWARD_TO_AGENT_STARTED = 'FORWARD_TO_AGENT_STARTED';
  static readonly FORWARD_TO_AGENT_FINISHED = 'FORWARD_TO_AGENT_FINISHED';
  static readonly UPDATE_VORVERTRAEGE_FOR_MODAL = 'UPDATE_VORVERTRAEGE_FOR_MODAL';
  static readonly START_FREMD_VERTRAEGE_AENDERN = 'START_FREMD_VERTRAEGE_AENDERN';
  static readonly FREMD_VERTRAEGE_AENDERN = 'FREMD_VERTRAEGE_AENDERN';
  static readonly UPDATE_VORVERTRAEGE = 'UPDATE_VORVERTRAEGE';
  static readonly UPDATE_FREMDE_VORVERTRAEGE = 'UPDATE_FREMDE_VORVERTRAEGE';
  static readonly UPDATE_FREMDE_STANDALONEPRODUKTE = 'UPDATE_FREMDE_STANDALONEPRODUKTE';
  static readonly UPDATE_STANDARD_VORVERTRAEGE = 'UPDATE_STANDARD_VORVERTRAEGE';
  static readonly UPDATE_STANDARD_STANDALONEPRODUKTE = 'UPDATE_STANDARD_STANDALONEPRODUKTE';
  static readonly UPDATE_FREMDVERTRAEGE_STARTED = 'UPDATE_FREMDVERTRAEGE_STARTED';
  static readonly UPDATE_FREMDVERTRAEGE_FINISHED = 'UPDATE_FREMDVERTRAEGE_FINISHED';
  static readonly UPDATE_WEITERE_VERTRAEGE_STARTED = 'UPDATE_WEITERE_VERTRAEGE_STARTED';
  static readonly UPDATE_WEITERE_VERTRAEGE_FINISHED = 'UPDATE_WEITERE_VERTRAEGE_FINISHED';
  static readonly CACHE_FREMD_VN_CHANGED = 'CACHE_FREMD_VN_CHANGED';
  static readonly SET_MULTIANGEBOT_VWG_ADRESS_VALIDATION_RESULT =
    'SET_MULTIANGEBOT_VWG_ADRESS_VALIDATION_RESULT';
  static readonly REQUEST_VORVERTRAG_HINWEISMODAL_WITH_OPTIONEN =
    'REQUEST_VORVERTRAG_HINWEISMODAL_WITH_OPTIONEN';
  static readonly VORVERTRAG_UMSTELLEN = 'VORVERTRAG_UMSTELLEN';
  static readonly WEITERE_VERTRAEGE_UBERNEHMEN = 'WEITERE_VERTRAEGE_UBERNEHMEN';
  static readonly WEITERE_VERTRAEGE_UBERNEHMEN_CACHE = 'WEITERE_VERTRAEGE_UBERNEHMEN_CACHE';
  static readonly UW_ENTRY_FILES_UPDATED = 'UW_ENTRY_FILES_UPDATED';
  static readonly FILE_DELETION_REQUESTED = 'FILE_DELETION_REQUESTED';
  static readonly FILE_DELETION_SUCCESSFUL = 'FILE_DELETION_SUCCESSFUL';
  static readonly FILE_DELETION_FAILED = 'FILE_DELETION_FAILED';
  static readonly FILE_MODIFICATION_FINISHED = 'FILE_MODIFICATION_FINISHED';
  static readonly FILE_MODIFICATION_IN_PROGRESS = 'FILE_MODIFICATION_IN_PROGRESS';
  static readonly REMOVE_NOT_EXISTING_FILES = 'REMOVE_NOT_EXISTING_FILES';
  static readonly UW_ENTRY_TEXTS_CHANGED = 'UW_ENTRY_TEXTS_CHANGED';
  static readonly UW_ENTRY_INITIALIZE = 'UW_ENTRY_INITIALIZE';
  static readonly UW_ENTRY_VERTRAG_ZEICHNEN_CHANGED = 'UW_ENTRY_VERTRAG_ZEICHNEN_CHANGED';
  static readonly UW_DECISION_MASSNAHME_ADDED = 'UW_DECISION_MASSNAHME_ADDED';
  static readonly UW_DECISION_MASSNAHME_REMOVED = 'UW_DECISION_MASSNAHME_REMOVED';
  static readonly UW_DECISION_MASSNAHME_UPDATED = 'UW_DECISION_MASSNAHME_UPDATED';
  static readonly UW_DECISION_DOKUMENTATION_CHANGED = 'UW_DECISION_DOKUMENTATION_CHANGED';
  static readonly UW_DECISION_UNDERWRITER_INFO_CHANGED = 'UW_DECISION_UNDERWRITER_INFO_CHANGED';
  static readonly UW_DECISION_UNDERWRITER_INFO_BATCH_CHANGED = 'UW_DECISION_UNDERWRITER_INFO_BATCH_CHANGED';
  static readonly UW_DECISION_SUBMITTED = 'UW_DECISION_SUBMITTED';
  static readonly UW_MASSNAHMEN_ACCEPTED = 'UW_MASSNAHMEN_ACCEPTED';
  static readonly UW_ENTRY_SNAPSHOT_RESTORE = 'UW_ENTRY_SNAPSHOT_RESTORE';
  static readonly DOWNLOAD_ELEMENTARFRAGEBOGEN_PDF = 'DOWNLOAD_ELEMENTARFRAGEBOGEN_PDF';
  static readonly ADD_FILE_DOWNLOAD_ERROR = 'ADD_FILE_DOWNLOAD_ERROR';
  static readonly REMOVE_FILE_DOWNLOAD_ERROR = 'REMOVE_FILE_DOWNLOAD_ERROR';
  static readonly REMOVE_FILE_DOWNLOAD_ERROR_BY_FILENAME = 'REMOVE_FILE_DOWNLOAD_ERROR_BY_FILENAME';
  static readonly CLEAR_FILE_DOWNLOAD_ERRORS = 'CLEAR_FILE_DOWNLOAD_ERRORS';
  static readonly ANGEBOTSDATUM_RESET_LOG = 'DASHBOARD_ANGEBOTSDATUM_RESET_LOG';

  handleHinweiseVorvertaege(): AnyAction {
    return { type: DashboardActions.HANDLE_HINWEISE_VORVERTRAEGE };
  }

  addNotification(notification: NotificationModel): DashboardAction<NotificationModel> {
    return {
      type: DashboardActions.ADD_NOTIFICATION,
      payload: notification,
    };
  }

  deleteAngebotRequested(payload: WarenkorbAngebot): DashboardAction<WarenkorbAngebot> {
    return {
      type: DashboardActions.DELETE_ANGEBOT_REQUESTED,
      payload,
    };
  }

  tgvPlaceholderAdded(): DashboardAction<void> {
    return { type: DashboardActions.TGV_PLACEHOLDER_ADDED };
  }

  tgvPlaceholderRemoved(): DashboardAction<void> {
    return { type: DashboardActions.TGV_PLACEHOLDER_REMOVED };
  }

  tgvGroupLaufzeitRequested(): DashboardAction<void> {
    return { type: DashboardActions.TGV_GROUP_LAUFZEIT_REQUESTED };
  }

  deleteNotification(notificationId: string): DashboardAction<{ notificationId: string }> {
    return {
      type: DashboardActions.DELETE_NOTIFICATION,
      payload: { notificationId },
    };
  }

  immobilienDialogOpen(
    appId: App,
    angebotsId = '',
  ): DashboardAction<{ appId: App; angebotsId: string }> {
    return {
      type: DashboardActions.IMMO_MODAL_OPEN,
      payload: { appId, angebotsId },
    };
  }

  immobilienDialogClose(): AnyAction {
    return { type: DashboardActions.IMMO_MODAL_CLOSE };
  }

  immoAusgewaehlt(immobilienId: string): DashboardAction<string> {
    return { type: DashboardActions.IMMO_AUSGEWAEHLT, payload: immobilienId };
  }

  neueImmoAusgewaehlt(isAuslandsImmobilie: boolean): DashboardAction<boolean> {
    return { type: DashboardActions.NEUE_IMMO_AUSGEWAEHLT, payload: isAuslandsImmobilie };
  }

  unfPersonenDialogOpen(angebotsId: WarenkorbAngebot['angebotsId']): AnyAction {
    return {
      type: DashboardActions.UNF_PERS_MODAL_OPEN,
      payload: { angebotsId },
    };
  }

  unfPersonenDialogClose(): AnyAction {
    return { type: DashboardActions.UNF_PERS_MODAL_CLOSE };
  }

  unfPersonAusgewaehlt(unfPersonenId: string): DashboardAction<string> {
    return { type: DashboardActions.UNF_PERS_AUSGEWAEHLT, payload: unfPersonenId };
  }

  standaloneProduktDialogOpen(
    payload: StandaloneDialogModel,
  ): DashboardAction<StandaloneDialogModel> {
    return {
      type: DashboardActions.STANDALONE_PRODUKT_MODAL_OPEN,
      payload: payload,
    };
  }

  requestStandaloneProduktDialog(payload: StandaloneDialogModel): DashboardAction<{
    modalActionType: string;
    actionPayload: StandaloneDialogModel;
  }> {
    return {
      type: DashboardActions.REQUEST_DASHBOARD_MODAL,
      payload: {
        modalActionType: DashboardActions.STANDALONE_PRODUKT_MODAL_OPEN,
        actionPayload: payload,
      },
    };
  }

  requestStandaloneEinschliessenDialog(payload: StandaloneProdukt): DashboardAction<{
    modalActionType: string;
    actionPayload: StandaloneProdukt;
  }> {
    return {
      type: DashboardActions.REQUEST_DASHBOARD_MODAL,
      payload: {
        modalActionType: DashboardActions.STANDALONE_EINSCHLIESSEN_MODAL_OPEN,
        actionPayload: payload,
      },
    };
  }

  requestEhvDialog(
    payload: Vorvertrag,
  ): DashboardAction<{ modalActionType: string; actionPayload: Vorvertrag }> {
    return {
      type: DashboardActions.REQUEST_DASHBOARD_MODAL,
      payload: {
        modalActionType: DashboardActions.EHV_MODAL_OPEN,
        actionPayload: payload,
      },
    };
  }

  requestRsZusammenfuehrenDialog(
    payload: Vorvertrag,
  ): DashboardAction<{ modalActionType: string; actionPayload: Vorvertrag }> {
    return {
      type: DashboardActions.REQUEST_DASHBOARD_MODAL,
      payload: {
        modalActionType: DashboardActions.RS_ZUSAMMENFUEHREN_MODAL_OPEN,
        actionPayload: payload,
      },
    };
  }

  requestForwardToUnderwriter(): AnyAction {
    return { type: DashboardActions.REQUEST_FORWARD_TO_UNDERWRITER };
  }

  forwardToUnderwriterStarted(): DashboardAction<void> {
    return { type: DashboardActions.FORWARD_TO_UNDERWRITER_STARTED };
  }

  forwardToUnderwriterFinished(): DashboardAction<void> {
    return { type: DashboardActions.FORWARD_TO_UNDERWRITER_FINISHED };
  }

  requestForwardToAgent(): AnyAction {
    return { type: DashboardActions.REQUEST_FORWARD_TO_AGENT };
  }

  forwardToAgentStarted(): DashboardAction<void> {
    return { type: DashboardActions.FORWARD_TO_AGENT_STARTED };
  }

  forwardToAgentFinished(): DashboardAction<void> {
    return { type: DashboardActions.FORWARD_TO_AGENT_FINISHED };
  }

  standaloneProduktDialogClose(): AnyAction {
    return { type: DashboardActions.STANDALONE_PRODUKT_MODAL_CLOSE };
  }

  standaloneEinschliessenDialogOpen(
    payload: StandaloneProdukt,
  ): DashboardAction<StandaloneProdukt> {
    return {
      type: DashboardActions.STANDALONE_EINSCHLIESSEN_MODAL_OPEN,
      payload: payload,
    };
  }

  rsZusammenfuehrenModalOpen(payload: Vorvertrag): DashboardAction<Vorvertrag> {
    return { type: DashboardActions.RS_ZUSAMMENFUEHREN_MODAL_OPEN, payload };
  }

  rsZusammenfuehrenModalClose(): AnyAction {
    return { type: DashboardActions.RS_ZUSAMMENFUEHREN_MODAL_CLOSE };
  }

  vermittlerwechselDialogOpen(): AnyAction {
    return {
      type: DashboardActions.VERMITTLERWECHSEL_MODAL_OPEN,
    };
  }

  vermittlerwechselDialogClose(): AnyAction {
    return {
      type: DashboardActions.VERMITTLERWECHSEL_MODAL_CLOSE,
    };
  }

  standaloneEinschliessenDialogClose(): AnyAction {
    return { type: DashboardActions.STANDALONE_EINSCHLIESSEN_MODAL_CLOSE };
  }

  ehvDialogOpen(payload: Vorvertrag): DashboardAction<Vorvertrag> {
    return {
      type: DashboardActions.EHV_MODAL_OPEN,
      payload: payload,
    };
  }

  ehvDialogClose(): AnyAction {
    return { type: DashboardActions.EHV_MODAL_CLOSE };
  }

  standaloneProduktEinschliessen(
    standaloneProduktEinschliessen: StandaloneProduktEinschliessen,
  ): DashboardAction<StandaloneProduktEinschliessen> {
    return {
      type: DashboardActions.STANDALONE_PRODUKT_EINSCHLIESSEN,
      payload: standaloneProduktEinschliessen,
    };
  }

  kraftRabattHinweisClosed(kraftRabattHinweisClosed: boolean): DashboardAction<boolean> {
    return {
      type: DashboardActions.KRAFT_RABATT_HINWEIS_CLOSED,
      payload: kraftRabattHinweisClosed,
    };
  }

  showSofortschutzHinweistext(showHinweistext: boolean): DashboardAction<boolean> {
    return {
      type: DashboardActions.SHOW_SOFORTSCHUTZ_HINWEISTEXT,
      payload: showHinweistext,
    };
  }

  unfallSelectedChanged(value: boolean): DashboardAction<boolean> {
    return { type: DashboardActions.UNFALL_SELECTED_CHANGED, payload: value };
  }

  newImmobilienReceived(immobilien: Array<Immobilie>): DashboardAction<Array<Immobilie>> {
    return {
      type: DashboardActions.NEW_IMMOBILIEN_RECEIVED,
      payload: immobilien,
    };
  }

  openPersonenSearchDialog(): SearchDialogContainerStateChangeAction<SearchlistItem> {
    return changeSearchDialogState({
      open: true,
      loading: false,
      error: false,
      confirmed: false,
    });
  }

  updateVorvertraegeForModal(fremdVertraege: {
    vorvertraege: Array<Vorvertrag>;
    standaloneprodukte: Array<StandaloneProdukt>;
  }): DashboardAction<{
    vorvertraege: Array<Vorvertrag>;
    standaloneprodukte: Array<StandaloneProdukt>;
  }> {
    return {
      type: DashboardActions.UPDATE_VORVERTRAEGE_FOR_MODAL,
      payload: fremdVertraege,
    };
  }

  finishFremdVertraegeAendern(
    aenderungen: Array<StandaloneProdukt | Vorvertrag>,
  ): DashboardAction<Array<StandaloneProdukt | Vorvertrag>> {
    return {
      type: DashboardActions.FREMD_VERTRAEGE_AENDERN,
      payload: aenderungen,
    };
  }

  startFremdVertraegeAendern(
    aenderungen: Array<StandaloneProdukt | Vorvertrag>,
  ): DashboardAction<Array<StandaloneProdukt | Vorvertrag>> {
    return {
      type: DashboardActions.START_FREMD_VERTRAEGE_AENDERN,
      payload: aenderungen,
    };
  }

  updateFremdeStandaloneProdukte(
    fremdVertraege: Array<OperatingStandaloneVertragsdaten>,
  ): DashboardAction<Array<OperatingStandaloneVertragsdaten>> {
    return {
      type: DashboardActions.UPDATE_FREMDE_STANDALONEPRODUKTE,
      payload: fremdVertraege,
    };
  }

  updateFremdeVorvertraege(
    fremdVertraege: Array<OperatingStandardVertragsdaten>,
  ): DashboardAction<Array<OperatingStandardVertragsdaten>> {
    return {
      type: DashboardActions.UPDATE_FREMDE_VORVERTRAEGE,
      payload: fremdVertraege,
    };
  }

  updateStandardStandaloneprodukte(
    vertraege: Array<StandaloneVertragsuebersicht>,
  ): DashboardAction<Array<StandaloneVertragsuebersicht>> {
    return {
      type: DashboardActions.UPDATE_STANDARD_STANDALONEPRODUKTE,
      payload: vertraege,
    };
  }

  updateStandardVorvertraege(
    vertraege: Array<StandardVertragsuebersicht>,
  ): DashboardAction<Array<StandardVertragsuebersicht>> {
    return {
      type: DashboardActions.UPDATE_STANDARD_VORVERTRAEGE,
      payload: vertraege,
    };
  }

  updateFremdvertraegeStarted(): DashboardAction<void> {
    return { type: DashboardActions.UPDATE_FREMDVERTRAEGE_STARTED };
  }

  updateFremdvertraegeFinished(): DashboardAction<void> {
    return { type: DashboardActions.UPDATE_FREMDVERTRAEGE_FINISHED };
  }

  updateWeitereVertraegeStarted(): DashboardAction<void> {
    return { type: DashboardActions.UPDATE_WEITERE_VERTRAEGE_STARTED };
  }

  updateWeitereVertraegeFinished(): DashboardAction<void> {
    return { type: DashboardActions.UPDATE_WEITERE_VERTRAEGE_FINISHED };
  }

  cacheFremdVnChanged(
    vnWechselAngabenElement: VnWechselVertraegeAendern,
  ): GlobalAction<VnWechselVertraegeAendern> {
    return {
      type: DashboardActions.CACHE_FREMD_VN_CHANGED,
      payload: vnWechselAngabenElement,
    };
  }

  setMultiangebotVwgAdressValidationResult(
    payload: MultiangebotAdressenValidationResult,
  ): DashboardAction<MultiangebotAdressenValidationResult> {
    return {
      type: DashboardActions.SET_MULTIANGEBOT_VWG_ADRESS_VALIDATION_RESULT,
      payload,
    };
  }

  requestVorvertragHinweismodalWithOptionen(
    textKey: ModalType,
    vertrag: Vorvertrag,
    hinweis: string,
  ): GlobalAction<{ type: ModalType; vertrag: Vorvertrag; hinweis: string }> {
    return {
      type: DashboardActions.REQUEST_VORVERTRAG_HINWEISMODAL_WITH_OPTIONEN,
      payload: { type: textKey, vertrag: vertrag, hinweis: hinweis },
    };
  }

  vorvertragUmstellen(vertrag: Vorvertrag): GlobalAction<Vorvertrag> {
    return {
      type: DashboardActions.VORVERTRAG_UMSTELLEN,
      payload: vertrag,
    };
  }

  saveSelectedProduct(produkt: WarenkorbAngebot): GlobalAction<AngebotsId> {
    return { type: GlobalActions.PRODUKT_IN_MODAL, produkt };
  }

  weitereVertreageUbernehmenCache(weitereVertraege: VertragItem[]): DashboardAction<VertragItem[]> {
    return {
      type: DashboardActions.WEITERE_VERTRAEGE_UBERNEHMEN_CACHE,
      payload: weitereVertraege,
    };
  }

  weitereVertreageUbernehmen(weitereVertraege: VertragItem[]): DashboardAction<VertragItem[]> {
    return {
      type: DashboardActions.WEITERE_VERTRAEGE_UBERNEHMEN,
      payload: weitereVertraege,
    };
  }

  uwEntryFilesUpdated(
    appId: App,
    angebotsId: string,
    contextKey: UwContextKey,
    uploadResults: UploadedFile[],
  ): DashboardAction<UwEntryFilesUpdatedPayload> {
    return {
      type: DashboardActions.UW_ENTRY_FILES_UPDATED,
      payload: {
        appId,
        angebotsId,
        contextKey,
        uploadResults,
      },
    };
  }

  deleteFile(
    documentId: string,
    fileUploaderId: string,
    angebotsId: string,
  ): { type: string; payload: { documentId: string; fileUploaderId: string; angebotsId: string } } {
    return {
      type: DashboardActions.FILE_DELETION_REQUESTED,
      payload: { documentId, fileUploaderId, angebotsId },
    };
  }

  elementarFragebogenfilesDeleted(
    extractedDocumentId: string,
    angebotsId: string,
    appId: App,
  ): DashboardAction<ElementarfragebogenFilesDeletedPayload> {
    return {
      type: DashboardActions.FILE_DELETION_SUCCESSFUL,
      payload: { extractedDocumentId, angebotsId, appId },
    };
  }

  fileModificationInProgress(): DashboardAction<void> {
    return {
      type: DashboardActions.FILE_MODIFICATION_IN_PROGRESS,
    };
  }

  fileModificationFinished(): DashboardAction<void> {
    return {
      type: DashboardActions.FILE_MODIFICATION_FINISHED,
    };
  }

  detectNotExistingFiles(documentIds: Array<string>): DashboardAction<Array<string>> {
    return {
      type: DashboardActions.REMOVE_NOT_EXISTING_FILES,
      payload: documentIds,
    };
  }

  uwEntryTextsChanged(
    appId: App,
    angebotsId: string,
    contextKey: UwContextKey,
    texts: Record<string, string>,
  ): DashboardAction<UwEntryTextsChangedPayload> {
    return {
      type: DashboardActions.UW_ENTRY_TEXTS_CHANGED,
      payload: {
        appId,
        angebotsId,
        contextKey,
        texts,
      },
    };
  }

  uwEntryInitialize(
    appId: App,
    angebotsId: string,
    contextKey: UwContextKey,
    risikoAddress: string,
    produktName: string,
    grund: string,
  ): DashboardAction<UwEntryInitializePayload> {
    return {
      type: DashboardActions.UW_ENTRY_INITIALIZE,
      payload: {
        appId,
        angebotsId,
        contextKey,
        risikoAddress,
        produktName,
        grund,
      },
    };
  }

  uwEntryVertragZeichnenChanged(
    appId: App,
    angebotsId: string,
    contextKey: UwContextKey,
    risikoAddress: string,
    vertragZeichnen: boolean | null,
  ): DashboardAction<UwEntryVertragZeichnenChangedPayload> {
    return {
      type: DashboardActions.UW_ENTRY_VERTRAG_ZEICHNEN_CHANGED,
      payload: {
        appId,
        angebotsId,
        contextKey,
        risikoAddress,
        vertragZeichnen,
      },
    };
  }

  uwDecisionMassnahmeAdded(
    appId: App,
    angebotsId: string,
    risikoAddress: string,
    massnahme: MeasureType,
  ): DashboardAction<UwDecisionMassnahmeAddedPayload> {
    return {
      type: DashboardActions.UW_DECISION_MASSNAHME_ADDED,
      payload: {
        appId,
        angebotsId,
        risikoAddress,
        massnahme,
      },
    };
  }

  uwDecisionMassnahmeRemoved(
    appId: App,
    angebotsId: string,
    risikoAddress: string,
    massnahmeIndex: number,
  ): DashboardAction<UwDecisionMassnahmeRemovedPayload> {
    return {
      type: DashboardActions.UW_DECISION_MASSNAHME_REMOVED,
      payload: {
        appId,
        angebotsId,
        risikoAddress,
        massnahmeIndex,
      },
    };
  }

  uwDecisionMassnahmeUpdated(
    appId: App,
    angebotsId: string,
    risikoAddress: string,
    massnahmeIndex: number,
    updates: Partial<UnderwriterMeasure>,
  ): DashboardAction<UwDecisionMassnahmeUpdatedPayload> {
    return {
      type: DashboardActions.UW_DECISION_MASSNAHME_UPDATED,
      payload: {
        appId,
        angebotsId,
        risikoAddress,
        massnahmeIndex,
        updates,
      },
    };
  }

  uwDecisionDokumentationChanged(
    appId: App,
    angebotsId: string,
    risikoAddress: string,
    dokumentation: string,
  ): DashboardAction<UwDecisionDokumentationChangedPayload> {
    return {
      type: DashboardActions.UW_DECISION_DOKUMENTATION_CHANGED,
      payload: {
        appId,
        angebotsId,
        risikoAddress,
        dokumentation,
      },
    };
  }

  uwDecisionUnderwriterInfoChanged(
    appId: App,
    angebotsId: string,
    field: UnderwriterIdentityField,
    value: string,
  ): DashboardAction<UwDecisionUnderwriterInfoChangedPayload> {
    return {
      type: DashboardActions.UW_DECISION_UNDERWRITER_INFO_CHANGED,
      payload: {
        appId,
        angebotsId,
        field,
        value,
      },
    };
  }

  uwDecisionUnderwriterInfoBatchChanged(
    updates: UwDecisionUnderwriterInfoBatchChangedPayload,
  ): DashboardAction<UwDecisionUnderwriterInfoBatchChangedPayload> {
    return {
      type: DashboardActions.UW_DECISION_UNDERWRITER_INFO_BATCH_CHANGED,
      payload: updates,
    };
  }

  uwDecisionSubmitted(
    decisions: UwDecisionSubmittedItem[],
  ): DashboardAction<UwDecisionSubmittedItem[]> {
    return {
      type: DashboardActions.UW_DECISION_SUBMITTED,
      payload: decisions,
    };
  }

  uwMassnahmenAccepted(): DashboardAction<void> {
    return { type: DashboardActions.UW_MASSNAHMEN_ACCEPTED };
  }

  downloadElementarfragebogenPdf(
    uploadId: string,
    fileName: string,
  ): DashboardAction<DownloadElementarfragebogenPdfPayload> {
    return {
      type: DashboardActions.DOWNLOAD_ELEMENTARFRAGEBOGEN_PDF,
      payload: { uploadId, fileName },
    };
  }

  addFileDownloadError(
    id: string,
    fileName: string,
    type: string,
  ): DashboardAction<FileDownloadError> {
    return { type: DashboardActions.ADD_FILE_DOWNLOAD_ERROR, payload: { id, fileName, type } };
  }

  removeFileDownloadError(errorId: string): DashboardAction<string> {
    return { type: DashboardActions.REMOVE_FILE_DOWNLOAD_ERROR, payload: errorId };
  }

  removeFileDownloadErrorByFileName(fileName: string): DashboardAction<string> {
    return { type: DashboardActions.REMOVE_FILE_DOWNLOAD_ERROR_BY_FILENAME, payload: fileName };
  }

  clearFileDownloadErrors(): AnyAction {
    return { type: DashboardActions.CLEAR_FILE_DOWNLOAD_ERRORS };
  }

  angebotsdatumResetLog(logEntries: AngebotsdatumResetLogEntry[]): DashboardAction<AngebotsdatumResetLogEntry[]> {
    return {
      type: DashboardActions.ANGEBOTSDATUM_RESET_LOG,
      payload: logEntries,
    };
  }

  uwEntrySnapshotRestore(snapshots: ProductUwSnapshot[]): DashboardAction<ProductUwSnapshot[]> {
    return {
      type: DashboardActions.UW_ENTRY_SNAPSHOT_RESTORE,
      payload: snapshots,
    };
  }
}
